import { useState, ReactNode, useRef, useEffect } from 'react'
import { useLocation, Navigate } from 'react-router-dom'
import { NewNavBar } from '../components/NewNavBar'
import { DeviceListSidebar, SIDEBAR_MIN_WIDTH, SIDEBAR_MAX_WIDTH, SIDEBAR_DEFAULT_WIDTH, SIDEBAR_WIDTH_KEY, SIDEBAR_COLLAPSED_WIDTH } from '../components/DeviceListSidebar'
import { BottomTabBar } from '../components/BottomTabBar'
import { SideRailNav } from '../components/SideRailNav'
import { DeviceFAB } from '../components/DeviceFAB'
import { MobileDeviceDrawer } from '../components/MobileDeviceDrawer'
import { useAuthContext, useAccountContext } from '../contexts'
import { useResponsiveLayout, LAYOUT_DIMENSIONS } from '../hooks/useResponsiveLayout'
import { isPublicPath, isChromelessPath } from '../config/publicRoutes'

interface NewMainLayoutProps {
  children: ReactNode
  showSidebar?: boolean
}

export function NewMainLayout({ children, showSidebar = true }: NewMainLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const isTabletViewport = window.innerWidth < 1440
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY)
    if (saved) {
      const parsed = parseInt(saved, 10)
      if (!isNaN(parsed) && parsed >= SIDEBAR_MIN_WIDTH && parsed <= SIDEBAR_MAX_WIDTH) {
        // On tablet-sized viewports, cap saved width to keep sidebar compact
        if (isTabletViewport) {
          return Math.min(parsed, SIDEBAR_MIN_WIDTH)
        }
        return parsed
      }
    }
    // Use tighter default on tablet-sized viewports
    if (isTabletViewport) {
      return SIDEBAR_MIN_WIDTH
    }
    return SIDEBAR_DEFAULT_WIDTH
  })
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false)
  const savedSidebarState = useRef<boolean>(false)
  const location = useLocation()
  const { isAuthorized } = useAuthContext()
  const { devices } = useAccountContext()

  // Get responsive layout info
  const layout = useResponsiveLayout()

  const deviceCount = Object.keys(devices).length

  const isAccountPage =
    location.pathname === '/profile' ||
    location.pathname === '/preferences' ||
    location.pathname === '/settings' ||
    location.pathname.startsWith('/settings/')

  // Save sidebar state when entering account pages, restore when leaving
  const wasOnAccountPage = useRef(false)
  useEffect(() => {
    if (isAccountPage && !wasOnAccountPage.current) {
      // Entering account page - save current state
      savedSidebarState.current = sidebarCollapsed
      wasOnAccountPage.current = true
    } else if (!isAccountPage && wasOnAccountPage.current) {
      // Leaving account page - restore saved state
      setSidebarCollapsed(savedSidebarState.current)
      wasOnAccountPage.current = false
    }
  }, [isAccountPage, sidebarCollapsed])

  const isPublicPage = isPublicPath(location.pathname)

  if (isAuthorized === false && !isPublicPage) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (isAuthorized === undefined && !isPublicPage) {
    return null
  }

  // Auth/entry pages and standalone legal documents render without app chrome.
  const isChromelessPage = isChromelessPath(location.pathname)

  const isAdminPage = location.pathname.startsWith('/admin')
  const isPartnerPage = location.pathname.startsWith('/partner')
  // Prefix match, not equality: the page is tabbed (/system-health/overview,
  // /system-health/endpoints) and an exact check would show the device sidebar
  // on every tab except the bare redirect path.
  const isSystemHealthPage =
    location.pathname === '/system-health' || location.pathname.startsWith('/system-health/')
  const isAlertsPage = location.pathname.startsWith('/admin-alerts')
  // Reports carry their own Report Panel. The device list is not a filter for
  // them — a report is about a group and a period, and picking a RIC changes
  // nothing on the page.
  const isReportsPage =
    location.pathname === '/reports' || location.pathname.startsWith('/reports/')

  const shouldShowSidebar = showSidebar && !isChromelessPage && !isAdminPage && !isPartnerPage && !isAccountPage && !isSystemHealthPage && !isAlertsPage && !isReportsPage && isAuthorized

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed)
  }

  const openMobileDrawer = () => {
    setMobileDrawerOpen(true)
  }

  const closeMobileDrawer = () => {
    setMobileDrawerOpen(false)
  }

  if (isChromelessPage) {
    return <div className="min-h-screen bg-gray-50">{children}</div>
  }

  const isMapPage = location.pathname === '/map' || location.pathname === '/'

  // Calculate main content padding based on layout
  const getMainPadding = () => {
    const padding: React.CSSProperties = {}

    if (layout.showBottomTabs) {
      // Add padding for bottom tabs
      padding.paddingBottom = LAYOUT_DIMENSIONS.BOTTOM_TAB_HEIGHT
    }

    if (layout.showSideRail) {
      // Add padding for side rail on right
      padding.paddingRight = LAYOUT_DIMENSIONS.SIDE_RAIL_WIDTH
    }

    if (layout.showPersistentSidebar && shouldShowSidebar) {
      // Desktop/tablet landscape - persistent sidebar on left
      padding.paddingLeft = sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : sidebarWidth
    }

    return padding
  }

  // Render mobile/tablet layout (phone portrait, phone landscape, tablet portrait)
  if (layout.isPhone || layout.isTabletPortrait) {
    // Calculate map dimensions accounting for navigation elements
    const mapHeight = layout.showBottomTabs
      ? `calc(100vh - ${LAYOUT_DIMENSIONS.BOTTOM_TAB_HEIGHT}px)`
      : '100vh'
    const mapWidth = layout.showSideRail
      ? `calc(100vw - ${LAYOUT_DIMENSIONS.SIDE_RAIL_WIDTH}px)`
      : '100vw'

    return (
      <div className={isMapPage ? 'min-h-screen overflow-hidden' : 'min-h-screen bg-gray-50'}>
        {/* Main content */}
        <main
          className="transition-all duration-300"
          style={isMapPage ? {} : getMainPadding()}
        >
          {isMapPage ? (
            // Full-bleed layout for map page - account for side rail/bottom tabs
            <div
              style={{
                height: mapHeight,
                width: mapWidth,
              }}
            >
              {children}
            </div>
          ) : (
            <div className="px-4 py-4">{children}</div>
          )}
        </main>

        {/* Device FAB - only show when sidebar is enabled */}
        {shouldShowSidebar && layout.showDeviceFAB && (
          <DeviceFAB
            onClick={openMobileDrawer}
            deviceCount={deviceCount}
            showBottomTabs={layout.showBottomTabs}
          />
        )}

        {/* Bottom Tab Navigation (portrait modes) */}
        {layout.showBottomTabs && <BottomTabBar />}

        {/* Side Rail Navigation (landscape phone) */}
        {layout.showSideRail && <SideRailNav />}

        {/* Mobile Device Drawer */}
        {shouldShowSidebar && (
          <MobileDeviceDrawer
            isOpen={mobileDrawerOpen}
            onClose={closeMobileDrawer}
          />
        )}
      </div>
    )
  }

  // Render tablet landscape layout
  if (layout.isTabletLandscape) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header navigation for tablet landscape */}
        <div className="fixed top-0 left-0 right-0 z-30">
          <NewNavBar onToggleSidebar={shouldShowSidebar ? toggleSidebar : undefined} />
        </div>

        <div className="pt-16">
          {/* Persistent sidebar on tablet landscape */}
          {shouldShowSidebar && (
            <DeviceListSidebar
              isCollapsed={sidebarCollapsed}
              onToggleCollapse={toggleSidebar}
              width={sidebarWidth}
              onWidthChange={setSidebarWidth}
            />
          )}

          <main
            className="transition-all duration-300"
            style={{
              paddingLeft: shouldShowSidebar
                ? sidebarCollapsed
                  ? SIDEBAR_COLLAPSED_WIDTH
                  : sidebarWidth
                : 0,
            }}
          >
            {isMapPage ? (
              <div className="h-[calc(100vh-4rem)]">{children}</div>
            ) : (
              <div className="mx-auto px-4 sm:px-6 lg:px-8 py-6">{children}</div>
            )}
          </main>
        </div>
      </div>
    )
  }

  // Render desktop layout (unchanged from original)
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="fixed top-0 left-0 right-0 z-30">
        <NewNavBar onToggleSidebar={shouldShowSidebar ? toggleSidebar : undefined} />
      </div>

      <div className="pt-16">
        {shouldShowSidebar && (
          <DeviceListSidebar
            isCollapsed={sidebarCollapsed}
            onToggleCollapse={toggleSidebar}
            width={sidebarWidth}
            onWidthChange={setSidebarWidth}
          />
        )}

        <main
          className="transition-all duration-300"
          style={{
            paddingLeft: shouldShowSidebar
              ? sidebarCollapsed
                ? SIDEBAR_COLLAPSED_WIDTH
                : sidebarWidth
              : 0,
          }}
        >
          {isMapPage ? (
            // Full-bleed layout for map page - no padding
            <div className="h-[calc(100vh-4rem)]">{children}</div>
          ) : (
            <div className="mx-auto px-4 sm:px-6 lg:px-8 py-6">{children}</div>
          )}
        </main>
      </div>
    </div>
  )
}
